import { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { posthog } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';

import type { CaptureOptions, Properties } from 'posthog-js';

import { ENV } from 'services/environment/environment';
import {
  domInterferenceBeforeSend,
  startDomInterferenceMonitor,
} from 'services/logging/domInterferenceMonitor/domInterferenceMonitor';
import { PartnerDTO, UserDTO } from 'support/types';

export const buildAnalyticsAttributes = ({ analyticsId, analyticsProperties }: AnalyticsProps) => {
  const props = Object.entries(analyticsProperties ?? {}).reduce((carry, [key, value]) => {
    const cleanKey = key.toLowerCase().replace(/[^a-zA-Z0-9]/g, '-');

    return { ...carry, [`data-ph-capture-attribute-${cleanKey}`]: value };
  }, {});

  return {
    ...props,
    'data-cheese-id': analyticsId,
    'data-ph-capture-attribute-analytics-id': analyticsId,
  };
};

export type AnalyticsProps = {
  analyticsId: string;
  analyticsProperties?: Record<string, string>;
};

export type TrackFunction = (event: string, properties?: Properties, options?: CaptureOptions) => void;

export const useAnalytics = () => {
  const posthog = usePostHog();

  const identify = useCallback(
    (user: UserDTO, partner: PartnerDTO) => {
      posthog?.identify(user.id, {
        email: user.email,
        name: user.name,
        language: user.language,
        partner: partner.name,
        industry: partner.industry,
        companyType: partner.companyType?.join(', '),
        partnerType: partner.partnerType,
        featureFlags: partner.features,
      });

      posthog?.group('tradePartner', partner.id, {
        name: partner.name,
        industry: partner.industry,
        companyType: partner.companyType?.join(', '),
        partnerType: partner.partnerType,
        featureFlags: partner.features,
      });
    },
    [posthog],
  );

  const reset = useCallback(() => {
    posthog?.reset();
  }, [posthog]);

  const track = useCallback(
    (event: string, properties?: Properties, options?: CaptureOptions) => {
      posthog?.capture(event, properties, options);
    },
    [posthog],
  );

  const pageView = useCallback(() => {
    track('$pageview', {
      $current_url: window.location.href,
    });
  }, [track]);

  const getSessionId = useCallback(() => {
    return posthog?.get_session_id();
  }, [posthog]);

  return useMemo(
    () => ({ identify, reset, track, pageView, getSessionId }),
    [identify, reset, track, pageView, getSessionId],
  );
};

export const isSentrySupportedEnvironment = () => {
  return ['production', 'staging', 'dev'].includes(ENV.BACKEND_ENV ?? '');
};

// Browser extensions reject non-Error "Request timeout <name>" values; each creates a new Sentry issue.
const SENTRY_IGNORED_ERRORS = [/Non-Error promise rejection captured with value: Request timeout /];

export const initializeSentry = () => {
  if (isSentrySupportedEnvironment() && ENV.SENTRY_DSN) {
    Sentry.init({
      dsn: ENV.SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        posthog.sentryIntegration({
          organization: 'procuros-gmbh',
          projectId: 4503999607799808,
          severityAllowList: ['error', 'info'], // optional: here is set to handle captureMessage (info) and captureException (error)
        }),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: [/^\//, ...(ENV.API_BASE_URL ? [ENV.API_BASE_URL] : [])],
      environment: ENV.SENTRY_ENVIRONMENT,
      release: ENV.SENTRY_RELEASE,
      sendDefaultPii: true,
      beforeSend: domInterferenceBeforeSend,
      ignoreErrors: SENTRY_IGNORED_ERRORS,
    });
    startDomInterferenceMonitor();
  }
};

export const track = (event: string, properties?: Properties, options?: CaptureOptions) => {
  posthog.capture(event, properties, options);
};

export const getSessionId = () => posthog?.get_session_id();
